import CloneUtils from "../utils/cloneUtils";

class DoubleMap<K extends string | number | symbol, V> {
    map: Partial<Record<K, V>>;
    constructor(map: Partial<Record<K, V>> = {}) {
        this.map = CloneUtils.deepClone(map);
    }

    add(key: K, val: V) {
        this.map[key] = val;
    }

    getValueByKey(key: K): V | null {
        const val = this.map[key];
        return val === undefined ? null : val;
    }

    getKeyByValue(val: V): string | null {
        /**
         * IMPORTANT: Object.keys() type coercion issue
         *
         * Object.keys() always returns string[], even when the actual keys are numbers or symbols.
         * This creates type mismatches that can cause runtime bugs, especially with strict equality
         * comparisons and array methods like includes().
         *
         * Example problem:
         * - Map has numeric keys: { 1: "user1", 2: "user2" }
         * - Object.keys() returns ["1", "2"] (strings)
         * - Comparing with number array: [1, 2].includes("1") → false
         *
         * Real case: targetedUsersMap in kpisChart.jsx filtering failed because:
         * - User IDs were stored as numbers in arrays
         * - Object.keys() returned string versions of IDs
         * - Array.includes() failed due to type mismatch (number vs string)
         *
         * Solution: Use for...in loop to preserve original key types
         *
         * @example
         * getKeyByValue(val: V): K | null {
         *   for (const key in this.map) {
         *     if (this.map[key] === val) {
         *       return key; // TypeScript correctly infers key as type K
         *     }
         *   }
         *   return null;
         * }
         */
        const key = Object.keys(this.map).find((k) => this.map[k as K] === val);
        return key === undefined ? null : key;
    }
}

export { DoubleMap };
